import { 
    ShareBaseDocument,
    DocumentRevision,
    ObjectType,
    UploadFile,
    UploadOptions,
    UploadStatus,
    UploadItem, 
} from 'phoenix/domain';
import { Action } from 'util/domain';
import { default as SettingsService } from 'phoenix/services/settingsService';
import { UploadItemFactory } from 'phoenix/factories/upload-item-factory';


/**
 * @ngdoc service
 * @name phoenix.upload.pxUploadService
 * 
 * @description
 * A method for uploading files and creating documents and document revisions.
 */
export class UploadService {
    public static $inject = [
        '$q',
        'settingsService', 
        'pxUploadItemFactory'
    ];

    /**
     * If options.maxParallelUploads has been reached, new files will
     * be queued and wait for any current uploads to be completed.
     */ 
    private fileQueue:UploadItem[] = [];
    /**
     * A list of subscribers that will be notified when a new upload process starts
     */ 
    private onStartSubs:Action[] = [];
    /**
     * A list of subscribers that will be notified when an upload process completes
     */
    private onCompleteSubs:Action[] = [];
    /**
     * A list of subscribers that will be notified when an upload process is cancelled.
     */
    private onCancelSubs:Action[] = [];
    /**
     * Stores all UploadItem objects that are actively being uploaded
     */
    private activeUploads:UploadItem[] = [];

    private readonly options:UploadOptions = {
        maxParallelUploads: 3,
        chunkThreshold: 1024 * 1024 * 5, // 5M
        chunkSize: 1024 * 512, // 500K
        maxRetries: 3,
        maxFileSizeInBytes: this.settingsService.maxFileSize * Math.pow(1000, 3)
    };

    /**
     * @ngdoc property
     * @name pxUploadService#uploadStatus
     * @propertyOf phoenix.upload.pxUploadService
     * @kind property
     * 
     * @description
     * Returns an instance of @see UploadStatus that represents
     * the status of the current upload process.
     */
    public readonly uploadStatus:UploadStatus = {
        filesQueued: 0,
        filesInProcess: 0,
        activeUploads: 0,
        totalBytesQueued: 0,
        bytesCompleted: 0,
        percentCompleted: 0,
        cancelMode: false
    };

    constructor(
        private $q:ng.IQService,
        private settingsService:SettingsService,
        private pxUploadItemFactory:UploadItemFactory
    ){
        for (let i = 0; i < this.options.maxParallelUploads; ++i) {
            this.activeUploads[i] = null;
        }
    }

    /**
     * @ngdoc method
     * @name pxUploadService#addFile
     * @methodOf phoenix.upload.pxUploadService
     * @kind method
     * 
     * @description
     * Upload a file that will be used to create a new ShareBase document.
     * 
     * @param {File} file The file to upload
     * @param {number} folderId Id of the folder the document will be added to
     * @param {string} uploadUri The url to use to create the document object.
     */
    public addFile(file:Blob, folderId:number, name:string, uploadUri:string) {
        return this.$q<ShareBaseDocument>((resolve, reject) => {
            let item = this.queueFile(file, folderId, ObjectType.FOLDER, name, uploadUri)
                .onComplete(resolve)
                .onError(reject);    
        });
    }

    /**
     * @ngdoc method
     * @name pxUploadService#replaceFile
     * @methodOf phoenix.upload.pxUploadService
     * @kind method
     * 
     * @description
     * Upload a file that will be used to create a new document revision.
     * 
     * @param {File} file The file object to upload
     * @param {number} documentId Id of the document the revision will be created under
     * @param {string} name Name of the new revision
     * @param {string} uploadUri The url to use to create the sharebase document revision object
     */
    public replaceFile(file:Blob, documentId:number, name:string, uploadUri:string) {
        return this.$q<ShareBaseDocument>((resolve, reject) => {
            this.queueFile(file, documentId, ObjectType.DOCUMENT, name, uploadUri)
                .onComplete(resolve)
                .onError(reject);        
        });        
    }

    /**
     * Creates a new upload item object based on parameters and queues it up to be sent to the server
     */
    private queueFile(file:Blob, objectId:number, objectType:ObjectType, name:string, uploadUri:string) {
        if (this.uploadStatus.cancelMode) {
            return;
        }

        if (file.size > this.options.maxFileSizeInBytes) {
           throw new Error('Upload limited exceeded');
        }

        let fileObj:UploadFile = {
            file,
            objectId,
            objectType,
            name,
            uploadUri
        }

        let emitStart = false;
        if (this.fileQueue.length == 0 && this.uploadStatus.activeUploads == 0) {
            // New upload process, reset the upload status, then set the emitStart
            // flag to true so we can notify subscribers.
            this.uploadStatus.filesQueued = 0;
            this.uploadStatus.filesInProcess = 0;
            this.uploadStatus.totalBytesQueued = 0;
            this.uploadStatus.bytesCompleted = 0;
            this.uploadStatus.percentCompleted = 0;
            this.uploadStatus.cancelMode = false;
            emitStart = true;
        }

        let item = this.pxUploadItemFactory.create(fileObj, this.processUpdate.bind(this), this.options);
        this.fileQueue.push(item);

        this.uploadStatus.filesQueued++;
        this.uploadStatus.totalBytesQueued += file.size;
        this.processUpdate(null);
        item.onProgress(percent => this.progressUpdate());

        if (emitStart) {
            this.onStartSubs.forEach(sub => sub());
        }

        return item;        
    }
    
    private processUpdate(completedItem?:UploadItem) {
        if (completedItem) {
            // we have a completed item.
            // update uploadStatus to reflect and remove
            // the item from activeUploads so a queued item can be uploaded
            if (this.activeUploads[completedItem.queueSlot]) {
                this.activeUploads[completedItem.queueSlot] = null;
                this.uploadStatus.activeUploads--;
                this.uploadStatus.bytesCompleted += completedItem.size;
                this.progressUpdate();
            }

        }

        // Start uploading a new item if there are any queued
        if (this.fileQueue.length) {
            for (let i = 0; i < this.activeUploads.length; ++i) {
                if (this.activeUploads[i] == null) {
                    let item = this.fileQueue.pop();
                    this.uploadStatus.activeUploads++;
                    this.uploadStatus.filesInProcess++;
                    this.activeUploads[i] = item;
                    item.queueSlot = i;
                    item.send();
                    break;
                }
            }
        }
        // If we have no active uploads at this point, then the upload process
        // has completed, notify all subscribers
        if (this.uploadStatus.activeUploads === 0) {
            this.onCompleteSubs.forEach(sub => sub());
        }
    };

    /**
     * @ngdoc method
     * @name pxUploadService#cancelAllUploads
     * @methodOf phoenix.upload.pxUploadService
     * @kind method
     * 
     * @description
     * Cancel all active and pending uploads.
     */
    public cancelAllUploads() {
        this.uploadStatus.cancelMode = true;
        this.fileQueue = [];
        this.activeUploads.forEach(item => {
            if (item) {
                item.cancel();
                this.activeUploads[item.queueSlot] = null
            }
        });

        this.uploadStatus.activeUploads = 0;
        this.onCancelSubs.forEach(sub => sub());
        this.uploadStatus.cancelMode = false;
    }

    /**
     * @ngdoc method
     * @name pxUploadService#onStart
     * @methodOf phoenix.upload.pxUploadService
     * @kind method
     * 
     * @description
     * Provide a callback function that will be invoked when a new upload process starts.
     * 
     * @param {Function} sub The callback to be invoked when new uploads start
     * @returns {Function} A function that can be called to unsubscribe from the onStart event.
     */
    public onStart(sub:Action) {
       this.onStartSubs.push(sub);
        
        return () => {
            let i = this.onStartSubs.indexOf(sub);
            if (i >= 0) {
                this.onStartSubs = [
                    ...this.onStartSubs.slice(0, i),
                    ...this.onStartSubs.slice(i + 1)
                ];
            }
        };
    }

    /**
     * @ngdoc method
     * @name pxUploadService#onComplete
     * @methodOf phoenix.upload.pxUploadService
     * @kind method
     * 
     * @description
     * Provide a callback function that will be invoked when an upload process completes.
     * 
     * @param {Function} sub The callback to be invoked when an upload process completes.
     * @returns {Function} A function that can be called to unsubscribe from the onComplete event.
     */
    public onComplete(sub:Action) {
        this.onCompleteSubs.push(sub);

        return () => {
            let i = this.onCompleteSubs.indexOf(sub);
            if (i >= 0) {
                this.onCompleteSubs = [
                    ...this.onCompleteSubs.slice(0, i),
                    ...this.onCompleteSubs.slice(i + 1)
                ];
            }
        };
    }

    public onCancel(sub:Action) {
        this.onCancelSubs.push(sub);

        return () => {
            let i = this.onCancelSubs.indexOf(sub);
            if (i >= 0) {
                this.onCancelSubs = [
                    ...this.onCancelSubs.slice(0, i),
                    ...this.onCancelSubs.slice(i+1)
                ];
            }
        };
    }

    // @TODO: This is an artifact from V1 port. I don't think
    // we need this. If it is not used when SBWEB-1142 is completed,
    // it should be removed.
    private get bytesUploaded() {
        let bytes = 0;
        for (let i = 0; i < this.activeUploads.length; ++i) {
            if (this.activeUploads[i] != null) {
                bytes += this.activeUploads[i].loaded;
            }
        }
        return bytes + this.uploadStatus.bytesCompleted;
    }

    /**
     *  Updates percent completed property on uploadStatus.
     */    
    private progressUpdate() {
        let loaded = this.bytesUploaded;
        let pc = (loaded / this.uploadStatus.totalBytesQueued) * 100;
        this.uploadStatus.percentCompleted = Math.round(pc);
    }


}


// WEBPACK FOOTER //
// ./src/phoenix/services/uploadService.ts